import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { FormFields, withNulls } from "./hrForm";
import { SIGN_IN_HINT, canEditLoginRole, loginFollowsProfile, personalFields, roleChangeOptions, signInChangeNotice } from "./employeeFields";
import { DetailGrid, LoadState, PageHeader, PrimaryButton, SecondaryButton } from "./hrComponents";
import { useHr } from "./HrGate";
import RoleControl from "./RoleControl";
import { cardStyle, formatDate, pageStyle, statusBadge, statusLabel, useLoad } from "./hrUi";

// The Employees capability every plan has: who the person is, their basic job details, and ending
// their employment. Everything else about an employee (statutory ids, bank, leave, history ...)
// belongs to the full HR module, so none of it is asked for or shown here.

const editableKeys = () => personalFields().map((f) => f.key);

function EditDrawer({ show, onHide, employee, employeeId, onDone, canEditRole = false }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const roleField = { key: "role", label: "Role", type: "select", options: roleChangeOptions(), hint: "What this login can do on the platform. Applies from their next sign-in." };
  // Work email / phone are also the sign-in for Admin, Support and HR logins.
  const withSignInHint = (f) => (loginFollowsProfile(employee.login_role) && (f.key === "work_email" || f.key === "work_phone") ? { ...f, hint: SIGN_IN_HINT } : f);
  useEffect(() => {
    if (show) {
      setValues({
        ...Object.fromEntries(editableKeys().filter((k) => employee[k] !== null && employee[k] !== undefined).map((k) => [k, employee[k]])),
        ...(canEditRole ? { role: Number(employee.login_role) } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const save = async () => {
    setSaving(true);
    try {
      const body = withNulls(values, editableKeys());
      // The role goes with the save only when it was actually changed.
      if (canEditRole && Number(values.role) !== Number(employee.login_role)) body.role = Number(values.role);
      const res = await hrmsService.updateEmployee(employeeId, body);
      if (res?.success) {
        toast.success([res.message || "Saved", signInChangeNotice(res)].filter(Boolean).join(" "));
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Edit employee"
      width={600}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      {canEditRole && Number(values.role) === 2 && Number(employee.login_role) !== 2 ? (
        <div className="mb-3" style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 8, padding: "10px 14px" }} role="alert">
          "Admin" gives this person full administrator access to your whole account - every screen, not just HR.
        </div>
      ) : null}
      <FormFields columns={2} values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={[...(canEditRole ? [roleField] : []), ...personalFields().map(withSignInHint)]} />
      <div style={{ fontSize: 12, color: "#6B7280" }}>The date of joining and employee code are fixed once set.</div>
    </SlidePanel>
  );
}

export default function TeamMemberProfile() {
  const { id } = useParams();
  const { access } = useHr();
  const [editing, setEditing] = useState(false);
  const [ending, setEnding] = useState(false);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getEmployee(id), [id]);
  const e = data?.employee;
  const canManage = !!access.is_tenant_admin || (access.permissions || []).includes("HR_ADMIN");
  const left = e?.status === "SEPARATED";

  const endEmployment = async () => {
    if (!window.confirm(`End ${e.full_name}'s employment today? Their login will be switched off.`)) return;
    setEnding(true);
    try {
      const res = await hrmsService.endEmployment(id);
      if (res?.success) {
        toast.success(res.message || "Employment ended");
        reload();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setEnding(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/hr/team" style={{ fontSize: 13 }}>
          ← Employees
        </Link>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload} rows={6}>
        {e ? (
          <>
            <PageHeader
              title={e.full_name}
              subtitle={`${e.employee_code}${e.designation_name ? ` · ${e.designation_name}` : ""}`}
              actions={
                canManage && !left ? (
                  <>
                    <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton>
                    <SecondaryButton onClick={endEmployment} disabled={ending}>
                      End employment
                    </SecondaryButton>
                  </>
                ) : null
              }
            />
            <div style={{ ...cardStyle, padding: 20 }}>
              <div className="mb-3">
                <span style={statusBadge(e.status)}>{statusLabel(e.status)}</span>
                {left && e.last_working_day ? <span style={{ fontSize: 13, color: "#6B7280", marginLeft: 8 }}>Left on {formatDate(e.last_working_day)}</span> : null}
              </div>
              <DetailGrid
                items={[
                  ["Login role", <RoleControl key="role" employeeId={id} role={e.login_role} canChange={!!access.is_tenant_admin && !left} onDone={reload} />],
                  ["Email", e.work_email || "—"],
                  ["Phone", e.work_phone || "—"],
                  ["Department", e.department_name || "—"],
                  ["Designation", e.designation_name || "—"],
                  ["Location", e.location_name || "—"],
                  ["Joined", formatDate(e.date_of_joining)],
                  ["Gender", e.gender || "—"],
                  ["Date of birth", e.date_of_birth ? formatDate(e.date_of_birth) : "—"],
                  ["Nationality", e.nationality || "—"],
                  ["Address", e.current_address || "—"],
                ]}
              />
            </div>
            <EditDrawer
              show={editing}
              onHide={() => setEditing(false)}
              employee={e}
              employeeId={id}
              onDone={reload}
              canEditRole={canEditLoginRole({ isTenantAdmin: access.is_tenant_admin, loginRole: e.login_role, status: e.status, employeeId: e.employee_id })}
            />
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
